import { getDisplayInfo } from '@/composable/useToolsFareOptimizer'
import { FareItems, ItemDescription } from '@/interfaces/entities/fareItems'
import { IFreeMarketAnalysisResponse, IReportOpportunity } from '@/interfaces/api/fareOptimizer'

// Definições de oportunidade espelhando EXATAMENTE o detector do backend
// (bem_rails · Reports::DetectOpportunitiesService::ITEMS). Lista narrativa para
// editor, recomendações e PDF. O gráfico usa waterfall_items + barra «Mercado Livre»
// espelhada desta lista (editável pelo diretor).
interface OpportunityDefinition {
  key: string
  categoria: string
  titulo: string
  descricao: string
  acao: string
}

const BANCO_CAPACITORES = 'Dimensionar e instalar banco de capacitores com controle automático para corrigir o fator de potência.'

export const OPPORTUNITY_DEFINITIONS: OpportunityDefinition[] = [
  {
    key: 'peak_reactive',
    categoria: 'Reativo Ponta',
    titulo: 'Energia reativa excedente (ponta)',
    descricao: 'Excedente de energia reativa no horário de ponta, tarifado por fator de potência abaixo de 0,92.',
    acao: BANCO_CAPACITORES,
  },
  {
    key: 'off_peak_reactive',
    categoria: 'Reativo Fora Ponta',
    titulo: 'Energia reativa excedente (fora ponta)',
    descricao: 'Excedente de energia reativa no horário fora de ponta, tarifado por fator de potência abaixo de 0,92.',
    acao: BANCO_CAPACITORES,
  },
  {
    key: 'unused_peak_demand',
    categoria: 'Demanda N Utilizada Ponta',
    titulo: 'Demanda contratada ociosa (ponta)',
    descricao: 'Demanda contratada de ponta acima do uso real — paga-se por capacidade que não é consumida.',
    acao: 'Reenquadrar a demanda contratada de ponta ao patamar medido, respeitando margem de segurança.',
  },
  {
    key: 'unused_off_peak_demand',
    categoria: 'Demanda N Utilizada Fora Ponta',
    titulo: 'Demanda contratada ociosa (fora ponta)',
    descricao: 'Demanda contratada fora de ponta acima do uso real — paga-se por capacidade que não é consumida.',
    acao: 'Reenquadrar a demanda contratada fora de ponta ao patamar medido, respeitando margem de segurança.',
  },
  {
    key: 'exceeded_peak_demand',
    categoria: 'Ultrapassagem Ponta',
    titulo: 'Ultrapassagem de demanda (ponta)',
    descricao: 'A demanda de ponta ultrapassou o contratado, gerando multa de até 2× a tarifa de demanda.',
    acao: 'Implantar gestão de carga e revisar a demanda contratada de ponta para eliminar a ultrapassagem.',
  },
  {
    key: 'exceeded_off_peak_demand',
    categoria: 'Ultrapassagem F Ponta',
    titulo: 'Ultrapassagem de demanda (fora ponta)',
    descricao: 'A demanda fora de ponta ultrapassou o contratado, gerando multa de até 2× a tarifa de demanda.',
    acao: 'Implantar gestão de carga e revisar a demanda contratada fora de ponta para eliminar a ultrapassagem.',
  },
  {
    key: 'exceeded_reactive_peak_demand',
    categoria: 'Demanda Reat Exced. Ponta',
    titulo: 'Demanda reativa excedente (ponta)',
    descricao: 'Excedente de demanda reativa no horário de ponta, cobrado por baixo fator de potência.',
    acao: 'Corrigir o fator de potência (banco de capacitores) para zerar o excedente de demanda reativa.',
  },
  {
    key: 'exceeded_reactive_off_peak_demand',
    categoria: 'Demanda Reat Exced. Fora Ponta',
    titulo: 'Demanda reativa excedente (fora ponta)',
    descricao: 'Excedente de demanda reativa no horário fora de ponta, cobrado por baixo fator de potência.',
    acao: 'Corrigir o fator de potência (banco de capacitores) para zerar o excedente de demanda reativa.',
  },
  {
    key: 'free_market',
    categoria: 'Mercado Livre',
    titulo: 'Migração para o mercado livre',
    descricao: 'A comparação entre o mercado cativo e o livre indica economia comprando energia diretamente de fornecedores.',
    acao: 'Avaliar elegibilidade ao ACL, levantar propostas com comercializadoras e simular o custo de migração.',
  },
]

export const FREE_MARKET_CATEGORY = 'Mercado Livre'
export const PEAK_WASTE_CATEGORY = 'Economia potencial'

/** Cores fixas por chave — mesmo mapeamento do gráfico legado OpportunityCalc. */
export const WATERFALL_BAR_COLORS: Record<string, string> = {
  peak_reactive: 'rgba(34, 144, 230, 0.5)',
  off_peak_reactive: 'rgba(34, 144, 34, 0.5)',
  unused_peak_demand: 'rgba(255, 69, 0, 0.5)',
  unused_off_peak_demand: 'rgba(255, 29, 0, 0.5)',
  exceeded_peak_demand: 'rgba(255, 69, 0, 0.5)',
  exceeded_off_peak_demand: 'rgba(60, 179, 113, 0.5)',
  exceeded_reactive_peak_demand: 'rgba(30, 144, 255, 0.5)',
  exceeded_reactive_off_peak_demand: 'rgba(60, 179, 113, 0.5)',
  free_market: 'rgba(60, 179, 113, 0.5)',
  peak_waste_potential_savings: 'rgba(0, 128, 0, 0.5)',
}

const WATERFALL_BAR_COLOR_FALLBACKS = [
  'rgba(34, 144, 230, 0.5)',
  'rgba(34, 144, 34, 0.5)',
  'rgba(255, 69, 0, 0.5)',
  'rgba(255, 29, 0, 0.5)',
  'rgba(60, 179, 113, 0.5)',
  'rgba(30, 144, 255, 0.5)',
  'rgba(153, 102, 255, 0.5)',
  'rgba(255, 159, 64, 0.5)',
]

export const getWaterfallBarColor = (key: string, fallbackIndex = 0): string =>
  WATERFALL_BAR_COLORS[key] ?? WATERFALL_BAR_COLOR_FALLBACKS[fallbackIndex % WATERFALL_BAR_COLOR_FALLBACKS.length]

const round2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100

const valueOf = (fareItems: FareItems, key: string): number => {
  const item = fareItems[key as keyof FareItems]
  return (item?.amount || 0) * (item?.price_per_unit || 0)
}

/** Rótulo de barra do waterfall: categoria conhecida ou displayName do fare item. */
export const getWaterfallBarLabel = (key: string): string => {
  const def = OPPORTUNITY_DEFINITIONS.find((d) => d.key === key)
  if (def) return def.categoria
  return getDisplayInfo(key).displayName
}

/** Valor monetário de um tariff_item para o waterfall (amount×preço; fallback value). */
export const waterfallItemValue = (item?: ItemDescription): number => {
  if (!item) return 0
  const calculated = (item.amount || 0) * (item.price_per_unit || 0)
  if (calculated > 0) return round2(calculated)
  if (!item.amount && !item.price_per_unit && item.value > 0) return round2(item.value)
  return 0
}

export const buildWaterfallChartBars = (
  waterfallItems: string[],
  fareItems: FareItems,
): { key: string; label: string; value: number }[] => {
  return waterfallItems.flatMap((key) => {
    const item = fareItems[key as keyof FareItems]
    const value = waterfallItemValue(item)
    if (value <= 0) return []
    return [{ key, label: getWaterfallBarLabel(key), value }]
  })
}

/** Oportunidade de mercado livre na lista curada/auto-detectada. */
export const findFreeMarketOpportunity = (
  opportunities: IReportOpportunity[],
): IReportOpportunity | undefined => opportunities.find((o) => o.categoria === FREE_MARKET_CATEGORY)

/** Barra extra do waterfall — valor paralelo a fare_items, espelha reportOpportunities. */
export const buildWaterfallFreeMarketBar = (
  opportunities: IReportOpportunity[],
): { key: string; label: string; value: number } | null => {
  const opp = findFreeMarketOpportunity(opportunities)
  const value = round2(opp?.economia_mensal || 0)
  if (value <= 0) return null
  return {
    key: 'free_market',
    label: opp?.categoria || FREE_MARKET_CATEGORY,
    value,
  }
}

export const waterfallKeyFromCategoria = (categoria: string, index: number): string => {
  const def = OPPORTUNITY_DEFINITIONS.find((d) => d.categoria === categoria)
  if (def) return def.key
  return `custom_${index}`
}

export const buildWaterfallOpportunityBars = (
  opportunities: IReportOpportunity[],
): { key: string; label: string; value: number }[] => {
  return opportunities.flatMap((opp, index) => {
    if (opp.categoria === FREE_MARKET_CATEGORY || opp.categoria === PEAK_WASTE_CATEGORY) return []
    const value = round2(opp.economia_mensal || 0)
    if (value <= 0) return []
    return [
      {
        key: waterfallKeyFromCategoria(opp.categoria, index),
        label: opp.categoria || opp.titulo || `custom_${index}`,
        value,
      },
    ]
  })
}

/** Barra informativa de economia potencial (peak_waste_analysis) — rótulo via i18n na view. */
export const buildWaterfallPeakWasteBar = (
  potentialSavings: number,
): { key: string; label: string; value: number } | null => {
  const value = round2(potentialSavings)
  if (value <= 0) return null
  return {
    key: 'peak_waste_potential_savings',
    label: '',
    value,
  }
}

export const buildPeakWasteOpportunity = (potentialSavings: number): IReportOpportunity | null => {
  const economiaMensal = round2(potentialSavings)
  if (economiaMensal <= 0) return null
  return buildOpportunity({
    categoria: PEAK_WASTE_CATEGORY,
    titulo: 'Redução de consumo em horário de ponta',
    descricao:
      'Parte do consumo no horário de ponta poderia ser deslocada para fora de ponta, gerando economia tarifária.',
    acao: 'Avaliar deslocamento de cargas do horário de ponta para o fora ponta.',
    economia_mensal: economiaMensal,
  })
}

export const withPeakWasteOpportunity = (
  opportunities: IReportOpportunity[],
  potentialSavings: number,
): IReportOpportunity[] => {
  if (opportunities.some((o) => o.categoria === PEAK_WASTE_CATEGORY)) {
    return opportunities
  }
  const peak = buildPeakWasteOpportunity(potentialSavings)
  if (!peak) return opportunities
  return [...opportunities, peak]
}

// Espelha deltaFreeMarket/hasFreeMarket do store: só há ganho quando o custo no
// mercado livre é menor que consumo + demanda medidos.
const freeMarketDelta = (fareItems: FareItems, freeMarketResult: IFreeMarketAnalysisResponse): number => {
  const freeMarketValue = freeMarketResult?.free_market?.value || 0
  if (freeMarketValue <= 0) return 0

  const sums =
    valueOf(fareItems, 'peak_consumption') +
    valueOf(fareItems, 'off_peak_consumption') +
    valueOf(fareItems, 'measured_peak_demand') +
    valueOf(fareItems, 'measured_off_peak_demand')
  const delta = sums - freeMarketValue
  return delta > 0 ? delta : 0
}

export const buildOpportunity = (partial: Partial<IReportOpportunity> = {}): IReportOpportunity => {
  const economiaMensal = round2(partial.economia_mensal || 0)
  return {
    categoria: partial.categoria ?? '',
    titulo: partial.titulo ?? '',
    descricao: partial.descricao ?? '',
    acao: partial.acao ?? '',
    economia_mensal: economiaMensal,
    economia_anual: partial.economia_anual != null ? round2(partial.economia_anual) : round2(economiaMensal * 12),
  }
}

// Lista inicial sugerida (mesma do detector do backend), já filtrando o que tem
// economia > 0. É só o ponto de partida: o diretor edita à vontade na tela.
export const buildDefaultOpportunities = (
  fareItems: FareItems,
  freeMarketResult: IFreeMarketAnalysisResponse,
): IReportOpportunity[] => {
  return OPPORTUNITY_DEFINITIONS.flatMap((def) => {
    const economiaMensal = round2(def.key === 'free_market' ? freeMarketDelta(fareItems, freeMarketResult) : valueOf(fareItems, def.key))
    if (economiaMensal <= 0) return []

    return [
      buildOpportunity({
        categoria: def.categoria,
        titulo: def.titulo,
        descricao: def.descricao,
        acao: def.acao,
        economia_mensal: economiaMensal,
      }),
    ]
  })
}
